import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from './Navbar';
// import Sidebar from './Sidebar';
import './Helpdesk.css';

const HelpDeskSupport = () => {
  const [subject, setSubject] = useState('');
  const [title, setTitle] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const [formId, setFormId] = useState('');
  const [formText, setFormText] = useState('');
  const [generalDescription, setGeneralDescription] = useState('');
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDetails, setShowTicketDetails] = useState(false);
  const [showCloseTicketPage, setShowCloseTicketPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [solution, setSolution] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  
  const [token, setToken] = useState(sessionStorage.getItem('token') || '');
 
  const [currentPage, setCurrentPage] = useState(1);
  const [ticketsPerPage] = useState(10);
  const [sbCollapsed, setSbCollapsed] = useState(() => {
    const saved = sessionStorage.getItem('dma_sb_collapsed');
    return saved !== null ? JSON.parse(saved) : false;
  });

  // Environment variable for API URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const API_URL = `${API_BASE_URL}/api`;

  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      (config) => {
        const token = sessionStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, []);

  // Save sidebar state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('dma_sb_collapsed', JSON.stringify(sbCollapsed));
  }, [sbCollapsed]);

  // Load tickets from backend on component mount
  useEffect(() => {
    fetchTicketsFromBackend();
    createTables();
  }, []);

  // Create tables if not exist
  const createTables = async () => {
    try {
      await axios.post(`${API_URL}/create-tables`);
      console.log('✅ Tables ready');
    } catch (error) {
      console.error('❌ Error creating tables:', error);
    }
  };

  const fetchTicketsFromBackend = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/tickets`);
      
      if (response.data && response.data.success) {
        const backendTickets = response.data.tickets || [];
        const reversedTickets = [...backendTickets].reverse();
        
        const formattedTickets = reversedTickets.map((ticket, index) => ({
          id: ticket.id,
          srNo: index + 1,
          ticketId: ticket.ticket_id || ticket.id?.toString() || (index + 1).toString(),
          type: 'ULB',
          officeName: 'DMA Office',
          subject: ticket.subject || 'No Subject',
          title: ticket.subject || '',
          description: ticket.description || '',
          relatedTo: ticket.form_type === 'form' ? 'Form' : 'General',
          form_name: ticket.form_name || '',
          due_date: ticket.due_date || '',
          status: ticket.status || 'Open',
          date: ticket.due_date ? new Date(ticket.due_date).toLocaleDateString('en-GB') : 
                 ticket.created_at ? new Date(ticket.created_at).toLocaleDateString('en-GB') : 
                 new Date().toLocaleDateString('en-GB'),
          created_at: ticket.created_at,
          updated_at: ticket.updated_at,
          closed_by: ticket.closed_by || null,
          closed_by_name: ticket.closed_by_name || null,
          closed_date: ticket.closed_date || null,
          action: 'View'
        }));
        
        setTickets(formattedTickets);
        setCurrentPage(1);
        if (selectedTicket) {
          const updatedTicket = formattedTickets.find(t => t.ticketId === selectedTicket.ticketId);
          if (updatedTicket) {
            setSelectedTicket(updatedTicket);
          }
        }
      }
    } catch (error) {
      console.error('❌ Error fetching tickets:', error);
      setMessage({ type: 'error', text: 'Failed to fetch tickets from server' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setLoading(false);
    }
  };

  const indexOfLastTicket = currentPage * ticketsPerPage;
  const indexOfFirstTicket = indexOfLastTicket - ticketsPerPage;
  const currentTickets = tickets.slice(indexOfFirstTicket, indexOfLastTicket);
  const totalPages = Math.ceil(tickets.length / ticketsPerPage);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleActionClick = async (ticket) => {
    try {
      const response = await axios.get(`${API_URL}/tickets/${ticket.ticketId}`);
      
      if (response.data && response.data.success) {
        const backendTicket = response.data.ticket;
        
        const detailedTicket = {
          ...ticket,
          subject: backendTicket.subject || ticket.subject,
          description: backendTicket.description || ticket.description,
          form_name: backendTicket.form_name || ticket.form_name,
          due_date: backendTicket.due_date || ticket.due_date,
          status: backendTicket.status || ticket.status,
          created_at: backendTicket.created_at,
          updated_at: backendTicket.updated_at,
          closed_by: backendTicket.closed_by || ticket.closed_by,
          closed_by_name: backendTicket.closed_by_name || ticket.closed_by_name,
          closed_date: backendTicket.closed_date || ticket.closed_date
        };
        
        setSelectedTicket(detailedTicket);
      } else {
        setSelectedTicket(ticket);
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      setSelectedTicket(ticket);
    }
    
    if (ticket.status === 'Open') {
      setShowTicketDetails(true);
      setShowCloseTicketPage(false);
      setSolution('');
      setSelectedFile(null);
      setConfirmed(false);
    } else {
      setShowTicketDetails(false);
      setShowCloseTicketPage(true);
    }
  };

  const handleCloseTicket = async () => {
    if (!confirmed) {
      alert('Please confirm that you have reviewed the details');
      return;
    }

    if (selectedTicket) {
      try {
        const response = await axios.put(`${API_URL}/tickets/${selectedTicket.ticketId}/status`, {
          status: 'Closed'
        });
        
        if (response.data && response.data.success) {
          await fetchTicketsFromBackend();
          
          const closedDate = response.data.ticket?.closed_date 
            ? new Date(response.data.ticket.closed_date).toLocaleString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
            : new Date().toLocaleString();
          
          const closedBy = response.data.updated_by?.username || 'Super Admin';
          
          alert(`✅ Ticket ${selectedTicket.ticketId} has been closed successfully on ${closedDate} by ${closedBy}`);
          setShowTicketDetails(false);
          setShowCloseTicketPage(true);
          setConfirmed(false);
        }
      } catch (error) {
        console.error('Error closing ticket:', error);
        alert('Failed to close ticket. Please try again.');
      }
    }
  };

  // Format date for display
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Back to tickets list
  const handleBackToTickets = () => {
    setShowTicketDetails(false);
    setShowCloseTicketPage(false);
    setSelectedTicket(null);
    setSolution('');
    setSelectedFile(null);
    setConfirmed(false);
  };

  // Handle file change
  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const Pagination = () => {
    if (tickets.length <= ticketsPerPage) return null;
    
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '25px',
        gap: '10px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={goToPreviousPage}
          disabled={currentPage === 1}
          style={{
            padding: '8px 16px',
            backgroundColor: currentPage === 1 ? '#6c757d' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          ← Previous
        </button>
        
        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => goToPage(page)}
              style={{
                padding: '8px 14px',
                backgroundColor: currentPage === page ? '#007bff' : '#f8f9fa',
                color: currentPage === page ? 'white' : '#495057',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: currentPage === page ? '600' : '400'
              }}
            >
              {page}
            </button>
          ))}
        </div>
        
        <button
          onClick={goToNextPage}
          disabled={currentPage === totalPages}
          style={{
            padding: '8px 16px',
            backgroundColor: currentPage === totalPages ? '#6c757d' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Next →
        </button>
      </div>
    );
  };

  const containerStyle = {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5'
  };

  const mainContentStyle = {
    flex: '1',
    padding: '20px',
    marginLeft: sbCollapsed ? '80px' : '250px',
    transition: 'margin-left 0.2s',
    paddingTop: '80px' // Space for fixed navbar
  };

  const headerStyle = {
    backgroundColor: '#ffffff',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  };

  const pageTitleStyle = {
    margin: '0',
    color: '#333',
    fontSize: '24px',
    fontWeight: '600'
  };

  const tableContainerStyle = {
    backgroundColor: '#ffffff',
    padding: '25px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '20px'
  };

  const thStyle = {
    backgroundColor: '#f8f9fa',
    padding: '12px',
    textAlign: 'left',
    borderBottom: '2px solid #dee2e6',
    fontWeight: '600',
    color: '#495057',
    fontSize: '14px'
  };

  const tdStyle = {
    padding: '12px',
    borderBottom: '1px solid #dee2e6',
    color: '#212529',
    fontSize: '14px',
    verticalAlign: 'middle'
  };

  const actionButtonStyle = {
    backgroundColor: '#007bff',
    color: 'white',
    padding: '6px 12px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500'
  };

  const statusBadgeStyle = (status) => ({
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: status === 'Open' ? '#d1ecf1' : '#d4edda',
    color: status === 'Open' ? '#0c5460' : '#155724'
  });

  // Loading indicator
  if (loading && tickets.length === 0) {
    return (
      <>
        <Navbar onMenuClick={() => setSbCollapsed(!sbCollapsed)} />
        <div style={containerStyle}>
          {/* <Sidebar collapsed={sbCollapsed} onCollapse={setSbCollapsed} /> */}
          <div style={mainContentStyle}>
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <div style={{
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #3498db',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }}></div>
              <p>Loading tickets from server...</p>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Page for Open Tickets
  const renderOpenTicketPage = () => {
    if (!selectedTicket) return null;

    return (
      <div style={{
        backgroundColor: '#ffffff',
        padding: '30px',
        borderRadius: '8px',
        marginTop: '20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '2px solid #007bff',
          paddingBottom: '15px'
        }}>
          <h2 style={{ margin: 0, color: '#333' }}>
            🎫 Ticket Details - {selectedTicket.ticketId}
          </h2>
          <button
            onClick={handleBackToTickets}
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            ← Back to Tickets
          </button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <div style={{ 
            backgroundColor: '#f8f9fa',
            padding: '20px',
            borderRadius: '6px',
            marginBottom: '20px'
          }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 2fr',
              gap: '12px',
              marginBottom: '15px'
            }}>
              <div style={{ fontWeight: '600', color: '#495057' }}>Ticket ID:</div>
              <div>{selectedTicket.ticketId}</div>
              
              <div style={{ fontWeight: '600', color: '#495057' }}>Subject:</div>
              <div>{selectedTicket.subject}</div>
              
              <div style={{ fontWeight: '600', color: '#495057' }}>Related To:</div>
              <div>{selectedTicket.relatedTo}</div>
              
              {selectedTicket.form_name && (
                <>
                  <div style={{ fontWeight: '600', color: '#495057' }}>Form Name:</div>
                  <div>{selectedTicket.form_name}</div>
                </>
              )}
              
              <div style={{ fontWeight: '600', color: '#495057' }}>Due Date:</div>
              <div>{selectedTicket.date}</div>
              
              <div style={{ fontWeight: '600', color: '#495057' }}>Created:</div>
              <div>{formatDateTime(selectedTicket.created_at)}</div>
              
              <div style={{ fontWeight: '600', color: '#495057' }}>Status:</div>
              <div>
                <span style={statusBadgeStyle(selectedTicket.status)}>
                  {selectedTicket.status}
                </span>
              </div>
            </div>
            
            <div style={{ 
              backgroundColor: '#e9ecef',
              padding: '15px',
              borderRadius: '4px',
              marginTop: '15px'
            }}>
              <div style={{ fontWeight: '600', color: '#495057', marginBottom: '10px' }}>
                Description:
              </div>
              <p style={{ margin: '0', lineHeight: '1.6' }}>
                {selectedTicket.description || selectedTicket.subject}
              </p>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ color: '#333' }}>Confirmed with above details? No change will be done</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
            <button
              onClick={handleCloseTicket}
              disabled={!confirmed}
              style={{
                backgroundColor: !confirmed ? '#6c757d' : '#dc3545',
                color: 'white',
                padding: '12px 30px',
                border: 'none',
                borderRadius: '4px',
                cursor: !confirmed ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '15px'
              }}
            >
              <span>🔒</span> Close Ticket
            </button>
            <button
              onClick={handleBackToTickets}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                padding: '12px 30px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '15px'
              }}
            >
              <span>✖️</span> Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Page for Closed Tickets
  const renderClosedTicketPage = () => {
    if (!selectedTicket) return null;

    return (
      <div style={{
        backgroundColor: '#ffffff',
        padding: '30px',
        borderRadius: '8px',
        marginTop: '20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '2px solid #28a745',
          paddingBottom: '15px'
        }}>
          <h2 style={{ margin: 0, color: '#333' }}>
            📜 Ticket History - {selectedTicket.ticketId}
          </h2>
          <button
            onClick={handleBackToTickets}
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            ← Back to Tickets
          </button>
        </div>

        <div style={{ 
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '6px',
          marginBottom: '20px'
        }}>
          <h3 style={{ color: '#28a745', marginBottom: '15px', fontSize: '20px' }}>
            {selectedTicket.subject}
          </h3>
          
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '15px',
            marginBottom: '20px'
          }}>
            <div>
              <p style={{ margin: '10px 0' }}><strong>Ticket ID:</strong> {selectedTicket.ticketId}</p>
              <p style={{ margin: '10px 0' }}><strong>Related to:</strong> {selectedTicket.relatedTo}</p>
              {selectedTicket.form_name && (
                <p style={{ margin: '10px 0' }}><strong>Form Name:</strong> {selectedTicket.form_name}</p>
              )}
              <p style={{ margin: '10px 0' }}><strong>Created:</strong> {formatDateTime(selectedTicket.created_at)}</p>
            </div>
            <div>
              <p style={{ margin: '10px 0' }}><strong>Status:</strong> 
                <span style={statusBadgeStyle(selectedTicket.status)}>
                  {selectedTicket.status}
                </span>
              </p>
              <p style={{ margin: '10px 0' }}>
                <strong>Closed Date:</strong> 
                <span style={{ color: '#28a745', fontWeight: '600', marginLeft: '5px' }}>
                  {formatDateTime(selectedTicket.closed_date)}
                </span>
              </p>
              {selectedTicket.closed_by_name && (
                <p style={{ margin: '10px 0' }}>
                  <strong>Closed By:</strong> {selectedTicket.closed_by_name}
                </p>
              )}
            </div>
          </div>

          <div style={{ 
            backgroundColor: '#e9ecef',
            padding: '15px',
            borderRadius: '4px',
            margin: '15px 0'
          }}>
            <p style={{ margin: '0 0 10px 0', fontWeight: '600' }}>Description:</p>
            <p style={{ margin: '0', lineHeight: '1.6' }}>
              {selectedTicket.description || selectedTicket.subject}
            </p>
          </div>

          <div style={{ 
            backgroundColor: '#d4edda',
            padding: '20px',
            borderRadius: '4px',
            marginTop: '20px',
            border: '1px solid #c3e6cb'
          }}>
            <p style={{ color: '#155724', margin: 0, fontSize: '15px', lineHeight: '1.6' }}>
              ✅ <strong>Ticket Resolution Summary:</strong> This ticket was successfully closed on{' '}
              <strong style={{ color: '#155724' }}>{formatDateTime(selectedTicket.closed_date)}</strong> by{' '}
              <strong style={{ color: '#155724' }}>{selectedTicket.closed_by_name || 'Super Admin'}</strong>.
            </p>
          </div>
        </div>

        <div style={{ 
          textAlign: 'center',
          padding: '20px',
          borderTop: '1px solid #dee2e6',
          marginTop: '20px'
        }}>
          <button
            onClick={handleBackToTickets}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              padding: '12px 40px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '15px'
            }}
          >
            <span>📋</span> Return to Tickets List
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Navbar onMenuClick={() => setSbCollapsed(!sbCollapsed)} />
      <div style={containerStyle}>
        {/* <Sidebar collapsed={sbCollapsed} onCollapse={setSbCollapsed} /> */}
        <div style={mainContentStyle}>
          <div style={headerStyle}>
            <h1 style={pageTitleStyle}>Help Desk & Support</h1>
            {message.text && (
              <div style={{
                marginTop: '15px',
                padding: '12px',
                backgroundColor: message.type === 'error' ? '#f8d7da' : '#d4edda',
                color: message.type === 'error' ? '#721c24' : '#155724',
                border: `1px solid ${message.type === 'error' ? '#f5c6cb' : '#c3e6cb'}`,
                borderRadius: '4px',
                fontSize: '14px'
              }}>
                {message.text}
              </div>
            )}
          </div>
          
          {/* ✅ फक्त Tickets Table दिसेल - WITH PAGINATION */}
          {!showTicketDetails && !showCloseTicketPage && (
            <div style={tableContainerStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#444', fontSize: '22px' }}>Tickets</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <span style={{ color: '#6c757d', fontSize: '14px' }}>
                    Showing {indexOfFirstTicket + 1}-{Math.min(indexOfLastTicket, tickets.length)} of {tickets.length}
                  </span>
                  <button
                    onClick={fetchTicketsFromBackend}
                    style={{
                      backgroundColor: '#6c757d',
                      color: 'white',
                      padding: '10px 20px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    <span>🔄</span> Refresh
                  </button>
                </div>
              </div>
              
              {tickets.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#6c757d' }}>
                  <p style={{ fontSize: '18px', marginBottom: '15px' }}>🎫 No tickets found</p>
                </div>
              ) : (
                <>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={thStyle}>Sr No.</th>
                          <th style={thStyle}>Ticket ID</th>
                          <th style={thStyle}>Type</th>
                          <th style={thStyle}>Subject</th>
                          <th style={thStyle}>Related to</th>
                          <th style={thStyle}>Status</th>
                          <th style={thStyle}>Due Date</th>
                          <th style={thStyle}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentTickets.map((ticket, index) => (
                          <tr key={ticket.srNo}>
                            <td style={tdStyle}>{indexOfFirstTicket + index + 1}</td>
                            <td style={tdStyle}>
                              <span style={{ color: '#007bff', fontWeight: '500' }}>
                                {ticket.ticketId}
                              </span>
                            </td>
                            <td style={tdStyle}>{ticket.type}</td>
                            <td style={tdStyle}>
                              <div style={{ maxWidth: '300px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {ticket.subject}
                              </div>
                            </td>
                            <td style={tdStyle}>{ticket.relatedTo}</td>
                            <td style={tdStyle}>
                              <span style={statusBadgeStyle(ticket.status)}>
                                {ticket.status}
                              </span>
                            </td>
                            <td style={tdStyle}>{ticket.date}</td>
                            <td style={tdStyle}>
                              <button 
                                onClick={() => handleActionClick(ticket)}
                                style={actionButtonStyle}
                              >
                                {ticket.action}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination />
                </>
              )}
            </div>
          )}

          {/* Show Open Ticket Page */}
          {showTicketDetails && renderOpenTicketPage()}
          
          {/* Show Closed Ticket Page */}
          {showCloseTicketPage && renderClosedTicketPage()}
        </div>
      </div>
    </>
  );
};

export default HelpDeskSupport;